// External dependencies.
import { LitElement, html } from "lit";
import { __ } from "@wordpress/i18n";
import swal from "sweetalert";

/**
 * AppNewsLetterForm component class.
 *
 * @since 0.9.0
 * @class AppNewsLetterForm
 * @extends {LitElement}
 */
class AppNewsLetterForm extends LitElement {
    /**
     * Creates an instance of AppNewsLetterForm.
     * 
     * @since 1.0.0
     * @memberof AppNewsLetterForm
     */
    constructor() {
        super();
        this.isSubmitting = false;
        this.handleSubmit = this.handleSubmit.bind( this );
        this.handleSuccess = this.handleSuccess.bind( this );
        this.handleError = this.handleError.bind( this );
    }

    /**
     * Inits the component's properties.
     *
     * @since 1.0.0
     * @readonly
     * @static
     * @memberof AppNewsLetterForm
     */
    static get properties() {
        return {
            nonce: { type: String },
            isSubmitting: { type: Boolean },
        };
    }

    /**
     * Handles the form element submit event.
     *
     * @since 1.0.0
     * @param {object} e
     * @memberof AppNewsLetterForm
     */
    handleSubmit( e ) {
        e.preventDefault();
        this.isSubmitting = true;
        const form = e.target;
        const url = form.getAttribute( 'action' );
        const formdata = new FormData( form );

        fetch( url, {
            method: 'POST',
            body: formdata,
        } )
            .then( res => res.json() )
            .then( this.handleSuccess )
            .catch( this.handleError );
    }

    /**
     * Handles the form success action.
     *
     * @since 1.0.0
     * @param {object} res
     * @memberof AppNewsLetterForm
     */
    handleSuccess( res ) {
        this.isSubmitting = false;

        if ( res.success ) {
            this.querySelector( 'form' ).reset();
            const text = ( 'already_susbcribed' === res.data.status ) ? __( 'Te has suscrito previamente.', 'tio-simon-foods' ) : __( 'Te has suscrito satisfactoriamente.', 'tio-simon-foods' );

            swal( {
                title: window.app.name,
                text,
                icon: 'success',
                buttons: {
                    confirm: {
                        className: 'btn btn-ts-primary',
                    }
                }
            } );

            return;
        }

        swal( {
            title: window.app.name,
            text: __( 'Proporcione una dirección de correo electrónico válida', 'tio-simon-foods' ),
            icon: 'warning',
            buttons: {
                confirm: {
                    className: 'btn btn-ts-primary',
                }
            }
        } );
    }

    /**
     * Handles the form error action.
     *
     * @since 1.0.0
     * @param {object} err
     * @memberof AppNewsLetterForm
     */
    handleError( err ) {
        console.log( err );
        this.isSubmitting = false;

        swal( {
            title: window.app.name,
            text: window.app.error_message,
            icon: 'error',
            buttons: {
                confirm: {
                    className: 'btn btn-ts-primary',
                }
            }
        } );
    }

    /**
     * Renders the component.
     *
     * @since 0.9.0
     * @returns TemplateResult
     * @memberof AppNewsLetterForm
     */
    render() {
        return html`
            <form action="${window.app.ajax_url}" method="POST" class="input-group-form" @submit="${this.handleSubmit}">
                <input type="hidden" name="action" value="handle_newsletter_form">
                <input type="hidden" name="_wpnonce" value="${this.nonce}">
                <div class="form-group mb-0">
                    <div class="input-group input-group-lg">
                        <input type="email" id="subscriber-email" class="form-control" name="email" placeholder="${__( 'Ingrese su dirección de correo electrónico', 'tio-simon-foods' )}" required>
                        <div class="input-group-append">
                            <button type="submit" class="btn btn-ts-primary" ?disabled="${this.isSubmitting}">${this.isSubmitting ? __( 'Envíando...', 'tio-simon-foods' ) : __( 'Suscribirse', 'tio-simon-foods' )}</button>
                        </div>
                    </div>
                </div>
            </form>
        `;
    }

    /**
     * Renders the template in the light DOM.
     *
     * @since 0.9.0
     * @returns this
     * @memberof AppNewsLetterForm
     */
    createRenderRoot() {
        return this;
    }
}

window.customElements.define( 'app-newsletter-form', AppNewsLetterForm );
